/**
 * The one-per-session `[LatencyCapability]` line: what rVFC metadata the guest's engine
 * actually populated, and which latency source that earned it.
 *
 * Safari's "no lag" is unverified because WebKit's rVFC metadata often omits
 * `rtpTimestamp`, leaving the host's `calibratedSource` null. This line is how a device run
 * records what the engine exposed, so a null source is traceable to the missing key rather
 * than guessed at afterwards. Pure formatting; the session decides when and where to emit.
 *
 * Browser-free and Node/Jest-safe.
 */

import type { GuestBrowserEngine } from '../adverseNetworkContract';
import type { GuestBrowserCapability, GuestLatencyCapability } from '../guestBrowserCapability';
import type { LatencySampleSource } from './framePipelineContracts';
import { selectLatencySource } from './latencySourceSelection';

export const LATENCY_CAPABILITY_LOG_TAG = '[LatencyCapability]' as const;

/** The shape of the line: the tag, the engine, the four probe flags and the chosen source. */
export type LatencyCapabilityLine = {
  tag: typeof LATENCY_CAPABILITY_LOG_TAG;
  /** `'unknown'` when the session was constructed without a capability. */
  engine: GuestBrowserEngine | 'unknown';
  hasRtpTimestamp: boolean;
  hasCaptureTime: boolean;
  hasReceiveTime: boolean;
  hasProcessingDuration: boolean;
  source: LatencySampleSource | null;
};

/** Format the line. Pure; never throws on a partial probe. */
export function formatLatencyCapabilityLine(
  engine: GuestBrowserEngine | undefined,
  probe: GuestLatencyCapability,
  source: LatencySampleSource | null,
): LatencyCapabilityLine {
  return {
    tag: LATENCY_CAPABILITY_LOG_TAG,
    engine: engine ?? 'unknown',
    hasRtpTimestamp: probe.hasRtpTimestamp === true,
    hasCaptureTime: probe.hasCaptureTime === true,
    hasReceiveTime: probe.hasReceiveTime === true,
    hasProcessingDuration: probe.hasProcessingDuration === true,
    source,
  };
}

/**
 * The source the guest will label its samples with, judged from the capability probe alone.
 *
 * The probe fires on the first rVFC frame, before that frame's observation is recorded, so
 * the call site has flags rather than a frame. The flags say which keys the frame carried;
 * the selection only ever asks whether a key is present, so a presence-only stand-in gives
 * the same answer the frame itself would.
 */
export function latencySourceForProbe(
  capability: GuestBrowserCapability | undefined,
  probe: GuestLatencyCapability,
  inputPhotonAvailable = false,
): LatencySampleSource | null {
  const PRESENT = 0;
  return selectLatencySource(
    capability,
    {
      ...(probe.hasRtpTimestamp ? { rtpTimestamp: PRESENT } : {}),
      ...(probe.hasCaptureTime ? { captureTime: PRESENT } : {}),
      ...(probe.hasReceiveTime ? { receiveTime: PRESENT } : {}),
    },
    inputPhotonAvailable,
  );
}
